import { useState, useEffect } from "react";
import { Box, Stack, Typography, Button, IconButton, Table, TableHead, TableRow, TableCell, TableBody, Avatar, LinearProgress, useTheme, alpha, CircularProgress } from "@mui/material";
import ReportLoader from "../../Reports/Loader";
import { colors, shadows } from "../source/source.styles";
import { ArrowLeft, Edit, Download, TrendingUp, Clock, Target, DollarSign } from "lucide-react";
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, RadarChart, PolarGrid, PolarAngleAxis, PolarRadiusAxis, Radar } from "recharts";
import { getEmployeeDetailedMetricsApi, getEmployeeGoalsApi } from "../../../api/reports.api";
import socket from "../../../utils/socket";

const EmployeeProfile = ({ employee, onBack }) => {
  const theme = useTheme();
  const primaryColor = theme.palette.primary.main;
  const [metrics, setMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  const [goals, setGoals] = useState(null);

  // Fetch employee detailed metrics
  useEffect(() => {
    const fetchEmployeeMetrics = async () => {
      if (!employee?.employeeId) return;

      try {
        setLoading(true);
        const response = await getEmployeeDetailedMetricsApi(
          employee.employeeId,
          {}
        );

        if (response?.data?.data) {
          setMetrics(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch employee metrics:", error);
      } finally {
        setLoading(false);
      }
    };

    const fetchGoals = async () => {
      if (!employee?.employeeId) return;

      try {
        const response = await getEmployeeGoalsApi(employee.employeeId);
        if (response?.data?.data) {
          setGoals(response.data.data);
        }
      } catch (error) {
        console.error("Failed to fetch employee goals:", error);
      }
    };

    fetchEmployeeMetrics();
    fetchGoals();

    // Connect to Socket.io for real-time updates
    socket.connect();

    socket.on("employee:metrics-updated", () => {
      fetchEmployeeMetrics();
    });

    socket.on("lead-status-updated", () => {
      fetchEmployeeMetrics();
    });

    return () => {
      socket.off("employee:metrics-updated");
      socket.off("lead-status-updated");
      socket.disconnect();
    };
  }, [employee?.employeeId]);

  // Transform metrics data for charts
  const activityData = metrics?.dailyActivity || [];

  const efficiencyData = metrics
    ? [
        {
          metric: "CALL EFFICIENCY",
          value: parseFloat(metrics.callEfficiency) || 0,
        },
        {
          metric: "CONVERSION RATE",
          value: parseFloat(metrics.conversionRate) || 0,
        },
        {
          metric: "ACTIVE LEADS",
          value: metrics.activeLeads
            ? (metrics.activeLeads / metrics.totalLeads) * 100
            : 0,
        },
        {
          metric: "VALID CALLS",
          value: metrics.totalCalls
            ? (metrics.validCalls / metrics.totalCalls) * 100
            : 0,
        },
      ]
    : [];

  const recentConversions =
    metrics?.recentConversions?.map((conv, idx) => ({
      id: idx + 1,
      leadName: conv.leadName,
      service: "Converted Lead",
      dealValue: `₹${conv.revenue?.toLocaleString()}`,
      date: conv.date,
    })) || [];

  const performanceGoals = metrics && goals
    ? goals.goals.map((goal) => {
        let current = 0;

        switch (goal.metric) {
          case "conversionRate":
            current = parseFloat(metrics.conversionRate) || 0;
            break;
          case "callEfficiency":
            current = parseFloat(metrics.callEfficiency) || 0;
            break;
          case "totalRevenue":
            current = metrics.totalRevenue || 0;
            break;
          case "activeLeads":
            current = metrics.activeLeads || 0;
            break;
          default:
            current = 0;
        }

        const percentage = goal.target > 0 ? (current / goal.target) * 100 : 0;

        return {
          label: goal.label,
          metric: goal.metric,
          current,
          target: goal.target,
          percentage: Math.min(percentage, 100),
          color: goal.color,
        };
      })
    : [];

  if (loading) {
    return <ReportLoader />;
  }

  const getAvatarColor = () => {
    return primaryColor;
  };

  const formatGoalValue = (value, metric) => {
    if (metric === "totalRevenue") {
      if (value >= 100000) {
        return `₹${(value / 100000).toFixed(1)}L`;
      }
      return `₹${(value / 1000).toFixed(0)}K`;
    }
    if (metric === "conversionRate" || metric === "callEfficiency") {
      return `${Math.round(value)}%`;
    }
    return Math.round(value);
  };

  return (
    <Box sx={{ bgcolor: colors.bgPrimary, minHeight: "100vh" }}>
      {/* Header */}
      <Box
        sx={{
          bgcolor: "#FFFFFF",
          borderBottom: `1px solid ${colors.border}`,
          p: 3,
        }}
      >
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Stack direction="row" alignItems="center" spacing={2}>
            <IconButton
              onClick={onBack}
              sx={{
                color: colors.textSecondary,
                "&:hover": {
                  bgcolor: colors.bgPrimary,
                },
              }}
            >
              <ArrowLeft size={20} />
            </IconButton>

            <Stack spacing={0.5} flex={1}>
              {/* Breadcrumb */}
              <Stack direction="row" alignItems="center" spacing={0.5}>
                <Typography
                  component="span"
                  onClick={onBack}
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                    cursor: "pointer",
                    "&:hover": { color: colors.primary },
                  }}
                >
                  REPORTS
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                  }}
                >
                  ›
                </Typography>
                <Typography
                  component="span"
                  onClick={onBack}
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                    cursor: "pointer",
                    "&:hover": { color: colors.primary },
                  }}
                >
                  EMPLOYEE REPORTS
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                  }}
                >
                  ›
                </Typography>
                <Typography
                  component="span"
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: primaryColor,
                    letterSpacing: 0.5,
                  }}
                >
                  {employee?.name?.toUpperCase() || "SARAH JOHNSON"}
                </Typography>
              </Stack>

              {/* Title */}
              <Typography
                sx={{
                  fontSize: 20,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                Employee Profile
              </Typography>
            </Stack>
          </Stack>

          <Stack direction="row" spacing={2}>
            <Button
              variant="outlined"
              startIcon={<Edit size={16} />}
              sx={{
                px: 2.5,
                py: 1,
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                borderColor: colors.border,
                color: colors.textPrimary,
                borderRadius: 2,
                "&:hover": {
                  borderColor: colors.border,
                  bgcolor: colors.bgPrimary,
                },
              }}
            >
              Edit Profile
            </Button>
            <Button
              variant="contained"
              startIcon={<Download size={16} />}
              sx={{
                px: 2.5,
                py: 1,
                fontSize: 13,
                fontWeight: 600,
                textTransform: "none",
                bgcolor: primaryColor,
                color: "#FFFFFF",
                borderRadius: 2,
                boxShadow: "none",
                "&:hover": {
                  bgcolor: theme.palette.primary.dark,
                  boxShadow: "none",
                },
              }}
            >
              Export Report
            </Button>
          </Stack>
        </Stack>
      </Box>

      {/* Content */}
      <Box sx={{ p: 4 }}>
        {/* Top Metrics */}
        <Stack direction="row" spacing={3} mb={4}>
          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                    mb: 1,
                  }}
                >
                  LEADS HANDLED
                </Typography>
                <Typography
                  sx={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: colors.textPrimary,
                    mb: 0.5,
                  }}
                >
                  {metrics?.totalLeads || 0}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.textSecondary,
                  }}
                >
                  {metrics?.activeLeads || 0} active leads
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: alpha(primaryColor, 0.15),
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <TrendingUp size={24} color={primaryColor} />
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                    mb: 1,
                  }}
                >
                  AVG. RESPONSE TIME
                </Typography>
                <Typography
                  sx={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: colors.textPrimary,
                    mb: 0.5,
                  }}
                >
                  {metrics?.avgResponseTime !== undefined ? `${metrics.avgResponseTime}m` : "N/A"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.textSecondary,
                  }}
                >
                  Average response
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: "#DBEAFE",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Clock size={24} color="#3B82F6" />
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                    mb: 1,
                  }}
                >
                  CONVERSION RATE
                </Typography>
                <Typography
                  sx={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: colors.textPrimary,
                    mb: 0.5,
                  }}
                >
                  {metrics?.conversionRate ? `${metrics.conversionRate}%` : "0%"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.textSecondary,
                  }}
                >
                  {metrics?.convertedLeads || 0} conversions
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: "#D1FAE5",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Target size={24} color="#10B981" />
              </Box>
            </Stack>
          </Box>

          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Box>
                <Typography
                  sx={{
                    fontSize: 11,
                    fontWeight: 600,
                    color: colors.textMuted,
                    letterSpacing: 0.5,
                    mb: 1,
                  }}
                >
                  REVENUE GENERATED
                </Typography>
                <Typography
                  sx={{
                    fontSize: 32,
                    fontWeight: 700,
                    color: colors.textPrimary,
                    mb: 0.5,
                  }}
                >
                  {metrics?.totalRevenue
                    ? metrics.totalRevenue >= 100000
                      ? `₹${(metrics.totalRevenue / 100000).toFixed(1)}L`
                      : `₹${(metrics.totalRevenue / 1000).toFixed(0)}K`
                    : "₹0"}
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    fontWeight: 600,
                    color: colors.textSecondary,
                  }}
                >
                  Total revenue generated
                </Typography>
              </Box>
              <Box
                sx={{
                  width: 48,
                  height: 48,
                  borderRadius: 2,
                  bgcolor: "#FEF3C7",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <DollarSign size={24} color="#F59E0B" />
              </Box>
            </Stack>
          </Box>
        </Stack>

        {/* Charts Section */}
        <Stack direction="row" spacing={3} mb={4}>
          {/* Daily Activity Trend */}
          <Box
            sx={{
              flex: 2,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
              <Box>
                <Typography
                  sx={{
                    fontSize: 16,
                    fontWeight: 700,
                    color: colors.textPrimary,
                    mb: 0.5,
                  }}
                >
                  Daily Activity Trend
                </Typography>
                <Typography
                  sx={{
                    fontSize: 13,
                    color: colors.textSecondary,
                  }}
                >
                  Total Workload Over Last 90 Days
                </Typography>
              </Box>
              <Stack direction="row" spacing={2} alignItems="center">
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 12,
                      height: 3,
                      bgcolor: primaryColor,
                      borderRadius: 1,
                    }}
                  />
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary }}>
                    Leads Handled
                  </Typography>
                </Stack>
                <Stack direction="row" alignItems="center" spacing={1}>
                  <Box
                    sx={{
                      width: 12,
                      height: 3,
                      bgcolor: "#CBD5E1",
                      borderRadius: 1,
                    }}
                  />
                  <Typography sx={{ fontSize: 11, fontWeight: 600, color: colors.textSecondary }}>
                    Calls Made
                  </Typography>
                </Stack>
              </Stack>
            </Stack>

            <ResponsiveContainer width="100%" height={250}>
              <LineChart data={activityData}>
                <CartesianGrid strokeDasharray="3 3" stroke={colors.borderLight} vertical={false} />
                <XAxis
                  dataKey="date"
                  tick={{ fontSize: 11, fill: colors.textSecondary }}
                  stroke={colors.border}
                  axisLine={false}
                />
                <YAxis
                  tick={{ fontSize: 11, fill: colors.textSecondary }}
                  stroke={colors.border}
                  axisLine={false}
                />
                <Tooltip />
                <Line
                  type="monotone"
                  dataKey="leadsHandled"
                  stroke={primaryColor}
                  strokeWidth={3}
                  dot={{ r: 4, fill: primaryColor }}
                  activeDot={{ r: 6 }}
                />
                <Line
                  type="monotone"
                  dataKey="callsMade"
                  stroke="#CBD5E1"
                  strokeWidth={2}
                  dot={{ r: 4, fill: "#CBD5E1" }}
                  activeDot={{ r: 6 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>

          {/* Efficiency Breakdown */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={2}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                Efficiency Breakdown
              </Typography>
              <IconButton size="small">
                <Download size={16} />
              </IconButton>
            </Stack>

            <Box sx={{ display: "flex", justifyContent: "center", my: 2 }}>
              <ResponsiveContainer width={280} height={280}>
                <RadarChart data={efficiencyData}>
                  <PolarGrid stroke={colors.borderLight} />
                  <PolarAngleAxis
                    dataKey="metric"
                    tick={{ fontSize: 10, fill: colors.textSecondary }}
                  />
                  <PolarRadiusAxis angle={90} domain={[0, 100]} tick={false} axisLine={false} />
                  <Radar
                    dataKey="value"
                    stroke={primaryColor}
                    fill={primaryColor}
                    fillOpacity={0.3}
                    strokeWidth={2}
                  />
                </RadarChart>
              </ResponsiveContainer>
            </Box>

            <Typography
              sx={{
                fontSize: 12,
                color: colors.textSecondary,
                textAlign: "center",
                mt: 1,
              }}
            >
              Overall Performance: {metrics?.efficiencyScore ? Math.round(parseFloat(metrics.efficiencyScore) * 100) : 0}/100
            </Typography>
          </Box>
        </Stack>

        {/* Bottom Section */}
        <Stack direction="row" spacing={3}>
          {/* Recent Conversions */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                Recent Conversions
              </Typography>
              <Typography
                sx={{
                  fontSize: 13,
                  fontWeight: 600,
                  color: colors.primary,
                  cursor: "pointer",
                  "&:hover": {
                    textDecoration: "underline",
                  },
                }}
              >
                View All
              </Typography>
            </Stack>

            <Table>
              <TableHead>
                <TableRow>
                  <TableCell
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      color: colors.textMuted,
                      textTransform: "uppercase",
                      borderBottom: `1px solid ${colors.borderLight}`,
                      py: 1.5,
                      bgcolor: colors.bgPrimary,
                    }}
                  >
                    Lead Name
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      color: colors.textMuted,
                      textTransform: "uppercase",
                      borderBottom: `1px solid ${colors.borderLight}`,
                      py: 1.5,
                      bgcolor: colors.bgPrimary,
                    }}
                  >
                    Service
                  </TableCell>
                  <TableCell
                    sx={{
                      fontSize: 11,
                      fontWeight: 700,
                      letterSpacing: 0.5,
                      color: colors.textMuted,
                      textTransform: "uppercase",
                      borderBottom: `1px solid ${colors.borderLight}`,
                      py: 1.5,
                      bgcolor: colors.bgPrimary,
                    }}
                    align="right"
                  >
                    Deal Value
                  </TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {recentConversions.map((conversion) => (
                  <TableRow
                    key={conversion.id}
                    sx={{
                      "&:hover": {
                        bgcolor: colors.bgPrimary,
                      },
                    }}
                  >
                    <TableCell
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: colors.textPrimary,
                        borderBottom: `1px solid ${colors.borderLight}`,
                        py: 2,
                      }}
                    >
                      <Stack direction="row" alignItems="center" spacing={1.5}>
                        <Avatar
                          sx={{
                            width: 28,
                            height: 28,
                            bgcolor: getAvatarColor(),
                            color: "#fff",
                            fontSize: 12,
                            fontWeight: 600,
                          }}
                        >
                          {conversion.leadName.split(" ").map((n) => n[0]).join("")}
                        </Avatar>
                        <Typography sx={{ fontSize: 13, fontWeight: 600, color: colors.textPrimary }}>
                          {conversion.leadName}
                        </Typography>
                      </Stack>
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 13,
                        fontWeight: 500,
                        color: colors.textSecondary,
                        borderBottom: `1px solid ${colors.borderLight}`,
                        py: 2,
                      }}
                    >
                      {conversion.service}
                    </TableCell>
                    <TableCell
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: colors.textPrimary,
                        borderBottom: `1px solid ${colors.borderLight}`,
                        py: 2,
                      }}
                      align="right"
                    >
                      {conversion.dealValue}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </Box>

          {/* Performance Goals */}
          <Box
            sx={{
              flex: 1,
              bgcolor: "#FFFFFF",
              borderRadius: 3,
              p: 3,
              border: `1px solid ${colors.borderLight}`,
              boxShadow: shadows.card,
            }}
          >
            <Stack direction="row" alignItems="center" justifyContent="space-between" mb={3}>
              <Typography
                sx={{
                  fontSize: 16,
                  fontWeight: 700,
                  color: colors.textPrimary,
                }}
              >
                Performance Goals
              </Typography>
              <Typography
                sx={{
                  fontSize: 11,
                  fontWeight: 600,
                  color: colors.textMuted,
                }}
              >
                October 2025
              </Typography>
            </Stack>

            <Stack spacing={3}>
              {performanceGoals.map((goal, index) => (
                <Box key={index}>
                  <Stack direction="row" alignItems="center" justifyContent="space-between" mb={1}>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 600,
                        color: colors.textPrimary,
                      }}
                    >
                      {goal.label}
                    </Typography>
                    <Typography
                      sx={{
                        fontSize: 13,
                        fontWeight: 700,
                        color: colors.textPrimary,
                      }}
                    >
                      {Math.round(goal.percentage)}% ({formatGoalValue(goal.current, goal.metric)}/{formatGoalValue(goal.target, goal.metric)})
                    </Typography>
                  </Stack>
                  <LinearProgress
                    variant="determinate"
                    value={goal.percentage}
                    sx={{
                      height: 8,
                      borderRadius: 1,
                      bgcolor: colors.bgPrimary,
                      "& .MuiLinearProgress-bar": {
                        bgcolor: goal.color,
                        borderRadius: 1,
                      },
                    }}
                  />
                </Box>
              ))}
            </Stack>
          </Box>
        </Stack>
      </Box>
    </Box>
  );
};

export default EmployeeProfile;
